import type { ComponentType } from 'react';
import type { ModuleId, ModuleMeta, ReportData, SubOptionLabelParts } from '../data/types';

import { identityModule } from './IdentityHeader';
import { scoreModule } from './Score';
import { overviewModule } from './Overview';
import { hasDirectorProfiles, managementModule } from './Management';
import { relatedPartiesModule } from './RelatedParties';
import { fundingModule } from './Funding';
import { mcaModule } from './MCA';
import { compliancesModule } from './Compliances';
import { legalRiskModule } from './LegalRisk';
import { hasConfirmedLegal, hasProbableLegal } from '../data/normalizers';
import { alertsModule } from './Alerts';
import { auditedStatementsModule } from './AuditedStatements';
import { bankingOverviewModule } from './BankingOverview';
import { financialMetricsModule } from './FinancialMetrics';
import { mcaChargesModule } from './McaCharges';
import { directorsDeepModule } from './DirectorsDeep';
import { directorBureauModule } from './DirectorBureau';
import { riskParametersModule } from './RiskParameters';
import { gstOverviewModule } from './GstOverview';
import { gstRevenueModule } from './GstRevenue';
import { gstExpensesModule } from './GstExpenses';
import { aggBankingModule } from './AggregatedBanking';
import {
    AUDITED_VERSIONS,
    CHARGE_DETAILS_LABEL,
    DIRECTOR_DETAILS_LABEL,
    DEBT_DETAILS_MODULE_TITLE,
    MCA_CHARGES_SUBSECTION_TITLE,
    CONFIRMED_MATCHES_LABEL,
    PROBABLE_MATCHES_LABEL,
    SUB_OPTION_CHARGE_DETAILS_ID,
    SUB_OPTION_CONFIRMED_ID,
    SUB_OPTION_DIRECTOR_DETAILS_ID,
    SUB_OPTION_PROBABLE_ID,
    BANKING_RAIL_TITLE,
    INDIVIDUAL_BANK_ANALYSIS_LABEL,
    SELECT_BANK_ACCOUNTS_LABEL,
    SUB_OPTION_INDIVIDUAL_BANKS_ID,
    bankAccountSubId,
    bankAccountSuffix,
} from '../constants';

export type SubOptionSpec = {
    id: string;
    label: string;
    labelParts?: SubOptionLabelParts;
    isDetail?: boolean;
    parentId?: string;
    childrenHeading?: string;
    isAvailable: (data: ReportData) => boolean;
};

export type ModuleScreenComponent = ComponentType<{ data: ReportData; index: string; options?: Record<string, boolean> }>;

const bankingSubOptions = (data: ReportData): SubOptionSpec[] => {
    const accounts = data.bankAccountAnalyses ?? [];
    if (!accounts.length) return [];
    return [
        {
            id: SUB_OPTION_INDIVIDUAL_BANKS_ID,
            label: INDIVIDUAL_BANK_ANALYSIS_LABEL,
            isDetail: true,
            childrenHeading: SELECT_BANK_ACCOUNTS_LABEL,
            isAvailable: () => true,
        },
        ...accounts.map((a) => ({
            id: bankAccountSubId(a.accountNumber),
            label: a.label,
            labelParts: { name: a.bankName, suffix: bankAccountSuffix(a.accountNumber) },
            isDetail: true,
            parentId: SUB_OPTION_INDIVIDUAL_BANKS_ID,
            isAvailable: () => true,
        })),
    ];
};

// Provisional-only and nature-less statements carry no versions, so the Financials card is a plain on/off card.
const auditedSubOptions = (data: ReportData): SubOptionSpec[] =>
    AUDITED_VERSIONS.some((v) => data.auditedStatements?.[v.hasKey])
        ? AUDITED_VERSIONS.map((v) => ({ id: v.id, label: v.label, isAvailable: (d) => !!d.auditedStatements?.[v.hasKey] }))
        : [];

// Sidecar registry: category label (top line on sidebar card) + Polus tabLockStatus key (if gated)
// + per-module coverage tags shown in the TOC. Kept here so individual module files stay slim.
// `defaultDisabled` modules still mount in the rail but start unselected — used during the spec-driven
// reorder so legacy modules can be re-enabled without losing their wiring.
export const MODULE_META_EXTRA: Record<
    ModuleId,
    {
        category: string;
        railTitle?: string;
        lockTabId?: string;
        coverage: string[];
        defaultDisabled?: boolean;
        subOptions?: SubOptionSpec[];
        dynamicSubOptions?: (data: ReportData) => SubOptionSpec[];
    }
> = {
    identity: {
        category: 'Company',
        coverage: ['Legal name', 'PAN', 'CIN', 'Sector', 'GSTINs', 'HQ address', 'Foundation date', 'Website'],
        defaultDisabled: true,
    },
    score: {
        category: 'Company',
        coverage: ['Composite AICA score', 'Per-pillar grades', 'Weakest drivers'],
    },
    riskParameters: {
        category: 'Risk',
        coverage: ['Heat-grid by category', 'Per-metric scoring', 'Severity bands'],
    },
    overview: {
        category: 'Company',
        lockTabId: 'overview',
        coverage: ['Business narrative', 'Products & customers', 'Geographies', 'Competitors', 'Sector outlook', 'News', 'Credit ratings'],
    },
    management: {
        category: 'Company',
        coverage: ['Directors with age, tenure, DIN', 'Stake & active status', 'Bureau & other boards', 'EPF headcount trend'],
        subOptions: [
            {
                id: SUB_OPTION_DIRECTOR_DETAILS_ID,
                label: DIRECTOR_DETAILS_LABEL,
                isDetail: true,
                isAvailable: hasDirectorProfiles,
            },
        ],
    },
    directorsDeep: {
        category: 'Management',
        lockTabId: 'creditBureau',
        coverage: ['CIBIL / Equifax bureau scores', 'Legal exposure', 'Concurrent directorships', 'Phone & contact'],
        defaultDisabled: true,
    },
    directorBureau: {
        category: 'Management',
        lockTabId: 'creditBureau',
        coverage: ['Per-director bureau scores', 'CIBIL / Equifax / Experian', 'DPD accounts', 'Current overdue', 'Max DPD'],
    },
    relatedParties: {
        category: 'Structure',
        lockTabId: 'relatedParty',
        coverage: ['Directors', 'Shareholders', 'Holdings', 'Subsidiaries', 'Sister concerns', 'Joint ventures', 'Shareholding pattern'],
    },
    funding: {
        category: 'Funding',
        coverage: ['Equity rounds', 'Debenture issuances', 'Instrument & amount per round', 'Total raised'],
    },
    mca: {
        category: 'Regulatory',
        coverage: ['Latest MCA filings summary', 'Filing status', 'Last refresh'],
    },
    mcaCharges: {
        category: DEBT_DETAILS_MODULE_TITLE,
        railTitle: MCA_CHARGES_SUBSECTION_TITLE,
        lockTabId: 'creditBureau',
        coverage: ['Open charges', 'Closed charges', 'Charge holder names', 'Total open exposure', 'Creditor concentration'],
        subOptions: [
            {
                id: SUB_OPTION_CHARGE_DETAILS_ID,
                label: CHARGE_DETAILS_LABEL,
                isDetail: true,
                isAvailable: (data) => !!data.mcaCharges?.rows.length,
            },
        ],
    },
    compliances: {
        category: 'Compliance',
        lockTabId: 'compliances',
        coverage: ['GSTR-1 monthly punctuality', 'GSTR-3B punctuality', 'EPF filing track record', 'Delay days per filing'],
    },
    financialMetrics: {
        category: 'Financials',
        coverage: [
            'Revenue',
            'Revenue growth %',
            'EBITA margin',
            'PAT',
            'Cash & equivalents',
            'Current assets/liabilities',
            'NWC',
            'LT/ST debt',
            'Tangible net worth',
        ],
    },
    auditedStatements: {
        category: 'Financials',
        lockTabId: 'auditedFinancials',
        coverage: [
            'Balance Sheet',
            'Profit & Loss',
            'Cashflow statement',
            'Financial Ratios',
            'Multi-year (5-year)',
            'Standalone / Consolidated',
        ],
        dynamicSubOptions: auditedSubOptions,
    },
    bankingOverview: {
        category: 'Financials',
        railTitle: BANKING_RAIL_TITLE,
        dynamicSubOptions: bankingSubOptions,
        // Polus task-tracker key for the banking & financials tab. The hook
        // reads `tabLockStatus[lockTabId]` to decide whether to mark the
        // module locked in the rail.
        lockTabId: 'bankingAndFinancials',
        coverage: ['Per-account closing balance', 'OD sanction limit', 'LTM transactions', 'Missing-day status'],
    },
    legalRisk: {
        category: 'Legal',
        lockTabId: 'legalChecks',
        coverage: ['Cases filed against', 'Cases filed by', 'Court & status per case', 'AML / PEP hits'],
        subOptions: [
            {
                id: SUB_OPTION_CONFIRMED_ID,
                label: CONFIRMED_MATCHES_LABEL,
                isAvailable: (data) => hasConfirmedLegal(data.legalRisk),
            },
            {
                id: SUB_OPTION_PROBABLE_ID,
                label: PROBABLE_MATCHES_LABEL,
                isAvailable: (data) => hasProbableLegal(data.legalRisk),
            },
        ],
    },
    alerts: {
        category: 'Risk',
        coverage: ['Severity-bucketed alerts', 'Category & reason', 'Alert date', 'Source entity'],
    },
    aggBanking: {
        category: 'Banking',
        coverage: [
            'Consolidated balances',
            'Debit & credit transactions',
            'Bounce transactions',
            'Cash and cheque',
            'Payment method breakdown',
            'OD/CC sanction limits',
        ],
    },
    gstOverview: {
        category: 'GST',
        coverage: [
            'Total GSTINs & active count',
            'HSN & SAC active',
            'Annual turnover range',
            'State-wise GST coverage',
            'GSTIN details table',
        ],
    },
    gstExpenses: {
        category: 'GST',
        coverage: ['GSTR-2A/2B summary table', 'Expense trend & growth', 'Vendor concentration', 'B2B vendor summary', 'ITC utilisation'],
    },
    gstRevenue: {
        category: 'GST',
        coverage: [
            'GSTR-3B summary table',
            'Revenue trend & growth',
            'Customer concentration',
            'B2B customer summary',
            'Revenue by segment & HSN',
        ],
    },
};

export type ModuleDefinition = {
    meta: ModuleMeta;
    hasData: (data: ReportData) => boolean;
    Screen: ModuleScreenComponent;
};

export const MODULES: Record<ModuleId, ModuleDefinition> = {
    identity: identityModule,
    score: scoreModule,
    riskParameters: riskParametersModule,
    overview: overviewModule,
    management: managementModule,
    directorsDeep: directorsDeepModule,
    directorBureau: directorBureauModule,
    relatedParties: relatedPartiesModule,
    funding: fundingModule,
    mca: mcaModule,
    mcaCharges: mcaChargesModule,
    compliances: compliancesModule,
    financialMetrics: financialMetricsModule,
    auditedStatements: auditedStatementsModule,
    bankingOverview: bankingOverviewModule,
    legalRisk: legalRiskModule,
    alerts: alertsModule,
    gstOverview: gstOverviewModule,
    gstRevenue: gstRevenueModule,
    gstExpenses: gstExpensesModule,
    aggBanking: aggBankingModule,
};

// Default-on stack first (in the order the V2 spec calls for), then the legacy / archived
// modules pinned to the bottom with `defaultDisabled: true` in MODULE_META_EXTRA so they
// surface in the rail unchecked. News & Sector Outlook / Key Competitors / Disclaimer slots
// are reserved in the spec but not yet built — they'll slot in around `riskParameters` and
// at the very end once their components land.
export const MODULE_ORDER: ModuleId[] = [
    // — Default visible (AICA Report order) —
    // `riskParameters`, `identity`, and `directorsDeep` removed from the report
    // (rail + PDF). Module files, MODULES entries, and MODULE_META_EXTRA stay
    // untouched so they can be re-enabled by adding the key back here.
    'overview',
    'financialMetrics',
    'score',
    'management',
    'relatedParties', // ← now titled "Structure & Relations"; renders shareholding inline
    'funding',
    'compliances',
    'legalRisk',
    'auditedStatements',
    'directorBureau',
    'gstOverview',
    'gstRevenue',
    'gstExpenses',
    'bankingOverview',
    // `aggBanking` no longer renders standalone — its content is embedded in
    // the Banking Overview section (after the accounts table).
    'mca',
    'mcaCharges',
    'alerts',
];
